import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthProvider';
import { ProjectProvider } from '@/project/ProjectContext';
import { RequireAuth } from '@/routes/RequireAuth';
import { RequirePartialAuth } from '@/routes/RequirePartialAuth';
import { AppLayout } from '@/layout/AppLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { SetupTotpPage } from '@/pages/auth/SetupTotpPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { LeadsPage } from '@/pages/LeadsPage';
import { BrandDnaPage } from '@/pages/BrandDnaPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { PlaceholderPage } from '@/pages/placeholders/PlaceholderPage';
import { MailboxesPage } from '@/pages/MailboxesPage';
import { InboxPage } from '@/pages/InboxPage';
import { CampaignsShell } from '@/features/campaigns/CampaignsShell';
import { CampaignHubPage } from '@/pages/campaigns/CampaignHubPage';
import { CampaignCommandPage } from '@/pages/campaigns/CampaignCommandPage';
import { CampaignLibraryPage } from '@/pages/campaigns/CampaignLibraryPage';
import { CampaignWizardPage } from '@/pages/campaigns/CampaignWizardPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { AccountsPage } from '@/pages/AccountsPage';
import { TeamPage } from '@/pages/TeamPage';
import { PipelinePage } from '@/pages/PipelinePage';
import { TasksPage } from '@/pages/TasksPage';
import { DeliverabilityPage } from '@/pages/DeliverabilityPage';
import { ScraperPage } from '@/pages/ScraperPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { DeveloperPage } from '@/pages/DeveloperPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { DoelgroepenPage } from '@/pages/doelgroepen/DoelgroepenPage';
import { DoelgroepDetailPage } from '@/pages/doelgroepen/DoelgroepDetailPage';
import { AutomationsPage } from '@/pages/AutomationsPage';
import { TracePage } from '@/pages/TracePage';
import { OpsPage } from '@/pages/OpsPage';
import { OrchestrationGraphPage } from '@/pages/OrchestrationGraphPage';
import { CopilotPage } from '@/pages/CopilotPage';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/2fa/setup"
            element={
              <RequirePartialAuth>
                <SetupTotpPage />
              </RequirePartialAuth>
            }
          />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <ProjectProvider>
                  <AppLayout />
                </ProjectProvider>
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="doelgroepen" element={<DoelgroepenPage />} />
            <Route path="doelgroepen/:audienceId" element={<DoelgroepDetailPage />} />
            <Route path="accounts" element={<AccountsPage />} />
            <Route path="bedrijfs-dna" element={<BrandDnaPage />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="taken" element={<TasksPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="campagnes" element={<CampaignsShell />}>
              <Route index element={<CampaignHubPage />} />
              <Route path="nieuw" element={<CampaignWizardPage />} />
              <Route path="bibliotheek" element={<CampaignLibraryPage />} />
              <Route path=":campaignId" element={<CampaignCommandPage />} />
            </Route>
            <Route path="inbox" element={<InboxPage />} />
            <Route path="scraper" element={<ScraperPage />} />
            <Route
              path="backlinks"
              element={<PlaceholderPage title="Backlinks" phase="fase 3" />}
            />
            <Route path="mailboxen" element={<MailboxesPage />} />
            <Route path="deliverability" element={<DeliverabilityPage />} />
            <Route path="integraties" element={<IntegrationsPage />} />
            <Route path="automations" element={<AutomationsPage />} />
            <Route path="copilot" element={<CopilotPage />} />
            <Route path="traces/:traceId" element={<TracePage />} />
            <Route path="orchestration" element={<OrchestrationGraphPage />} />
            <Route path="admin/ops" element={<OpsPage />} />
            <Route path="developer" element={<DeveloperPage />} />
            <Route path="instellingen" element={<SettingsPage tab="privacy" />} />
            <Route path="instellingen/compliance" element={<SettingsPage tab="compliance" />} />
            <Route path="instellingen/privacy" element={<SettingsPage tab="privacy" />} />
            <Route path="instellingen/audit" element={<SettingsPage tab="audit" />} />
            <Route path="instellingen/platform" element={<SettingsPage tab="platform" />} />
          </Route>
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
